/**
 * Business settings — docs/11 §Setup → Settings.
 *
 * Every one of these is a switch the business throws, not a deployment
 * decision, so each carries its own explanation in the words the operator
 * reads. The registry is the single list: the screen renders it, the server
 * validates against it, and `effect` records where in the code the setting is
 * actually honoured — a toggle that changes nothing is worse than no toggle.
 */

export type SettingKind = 'boolean' | 'number' | 'text' | 'select' | 'lookup'

export interface SettingDef {
  readonly key: string
  readonly group: string
  readonly label: string
  /** The paragraph under the switch, in the business's own terms. */
  readonly blurb: string
  readonly kind: SettingKind
  readonly defaultValue: boolean | number | string
  readonly options?: ReadonlyArray<{ value: string; label: string }>
  readonly lookup?: { entity: string }
  /**
   * A setting that cannot be taken back once given — the observed system says
   * so in as many words about quantity precision.
   */
  readonly irreversible?: boolean
  /** Where this is honoured. Empty means nothing reads it yet. */
  readonly effect: string
}

const def = <const T extends SettingDef>(s: T): T => s

export const SETTINGS = [
  /* ---- inventory --------------------------------------------------- */
  def({
    key: 'inventory_enabled',
    group: 'Inventory',
    label: 'Start using inventory',
    blurb: 'You can start managing inventory. Applicable only to new projects.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'The inventory document types appear on the Transactions hub.',
  }),
  def({
    key: 'site_inventory_enabled',
    group: 'Inventory',
    label: 'Enable project site inventory management',
    blurb:
      'Projects can have a site store, and you can check the inventory in it. Material in and out of the site store is recorded.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Site inventory records and material transfers to site appear on the Transactions hub.',
  }),
  def({
    key: 'purchase_plan_in_material_request',
    group: 'Inventory',
    label: "Enable 'Set up purchase plan' in material requests",
    blurb:
      'Based on the stock available in the warehouse, the stores team can decide how much has to be purchased for a material request.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'An approved material request joins the purchase-plan work list until a plan is set, and no purchase order may be raised from it before then.',
  }),

  def({
    key: 'stock_count_full_days',
    group: 'Inventory',
    label: 'Count every store in full every … days',
    blurb: 'How often a full stock audit is due for each warehouse and site store. 30 is monthly; 7 is weekly.',
    kind: 'number',
    defaultValue: 30,
    effect: 'The stock audit status report shows a store as due once its last full count is this old.',
  }),
  def({
    key: 'stock_count_a_days',
    group: 'Inventory',
    label: 'Cycle-count class A every … days',
    blurb: 'High-value materials are counted more often than the rest. Weekly is usual.',
    kind: 'number',
    defaultValue: 7,
    effect: 'The stock audit status report shows class A as due once its last count is this old.',
  }),
  def({
    key: 'stock_count_b_days',
    group: 'Inventory',
    label: 'Cycle-count class B every … days',
    blurb: 'Valves and pipes, the middle of the range: monthly is usual.',
    kind: 'number',
    defaultValue: 30,
    effect: 'The stock audit status report shows class B as due once its last count is this old.',
  }),
  def({
    key: 'stock_count_c_days',
    group: 'Inventory',
    label: 'Cycle-count class C every … days',
    blurb: 'Fittings and consumables: quarterly is usual.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The stock audit status report shows class C as due once its last count is this old.',
  }),

  /* ---- material planning (docs/24 §3.2) ------------------------------ */
  def({
    key: 'abc_a_value_share',
    group: 'Material planning',
    label: 'Class A covers the first … % of value',
    blurb:
      'Rank the materials by value, most expensive first. Those making up this share of the total are class A — high value.',
    kind: 'number',
    defaultValue: 70,
    effect: 'The ABC analysis report suggests class A up to this cumulative share.',
  }),
  def({
    key: 'abc_b_value_share',
    group: 'Material planning',
    label: 'Class B runs on to … % of value',
    blurb: 'After class A, the materials up to this cumulative share are class B. Everything after is class C.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The ABC analysis report suggests class B up to this cumulative share.',
  }),
  def({
    key: 'abc_a_lead_days',
    group: 'Material planning',
    label: 'A lead time of … days or more makes a material class A',
    blurb:
      'A water tank that takes two months to make is class A even if it is not expensive: it has to be ordered first.',
    kind: 'number',
    defaultValue: 45,
    effect: 'The ABC analysis report lifts a material to class A when its lead time is this long.',
  }),
  def({
    key: 'abc_b_lead_days',
    group: 'Material planning',
    label: 'A lead time of … days or more makes a material at least class B',
    blurb: 'Valves and pipes that take a fortnight to arrive are class B whatever they cost.',
    kind: 'number',
    defaultValue: 10,
    effect: 'The ABC analysis report lifts a material to class B when its lead time is this long.',
  }),
  def({
    key: 'mrp_buffer_days',
    group: 'Material planning',
    label: 'Order … days earlier than the lead time alone says',
    blurb:
      'A margin for approvals, transport and the unexpected. The requirement plan orders this many days before need-by less lead time.',
    kind: 'number',
    defaultValue: 3,
    effect: 'The material requirement plan subtracts it, with the lead time, from each need-by date.',
  }),
  def({
    key: 'lead_time_a_days',
    group: 'Material planning',
    label: 'Lead time for class A, in days',
    blurb: 'Used for a class A material when neither the item nor its category gives a lead time of its own.',
    kind: 'number',
    defaultValue: 75,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_b_days',
    group: 'Material planning',
    label: 'Lead time for class B, in days',
    blurb: 'Used for a class B material when neither the item nor its category gives a lead time of its own.',
    kind: 'number',
    defaultValue: 20,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_c_days',
    group: 'Material planning',
    label: 'Lead time for class C, in days',
    blurb: 'Fittings and consumables, bought locally. Used when neither the item nor its category says.',
    kind: 'number',
    defaultValue: 3,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),
  def({
    key: 'lead_time_s_days',
    group: 'Material planning',
    label: 'Lead time for class S, in days',
    blurb: 'Special and customised materials: made to the customer’s specification, so usually the longest wait.',
    kind: 'number',
    defaultValue: 90,
    effect: 'The material requirement plan and the purchase requisition read it as the lead time.',
  }),

  /* ---- vendor selection (docs/24 §3.5) ------------------------------- */
  def({
    key: 'cs_price_weight_pct',
    group: 'Vendor selection',
    label: 'How much of the comparison is price, in %',
    blurb:
      'The comparative statement scores each quotation out of 100: this share on price, the rest on '
      + 'the supplier’s latest evaluation — quality, delivery, compliance and the rest. 100 compares on price alone.',
    kind: 'number',
    defaultValue: 70,
    effect: 'The comparative statement weighs price and vendor score by it, and recommends the highest.',
  }),

  /* ---- estimation and rates ---------------------------------------- */
  def({
    key: 'quantity_decimals',
    group: 'Estimation and rates',
    label: 'Decimal precision for quantities',
    blurb:
      'The number of decimal places quantities are recorded to. Two is the default; three suits cable and pipe work. Changing this is irreversible.',
    kind: 'select',
    defaultValue: '3',
    options: [{ value: '2', label: '2 decimals' }, { value: '3', label: '3 decimals' }],
    irreversible: true,
    effect: 'Quantities are rounded and displayed to this precision.',
  }),
  def({
    key: 'show_estimated_rate_to_purchase_team',
    group: 'Estimation and rates',
    label: 'Show the estimated rate to the purchase team',
    blurb:
      'When this is on, the estimated rate is visible to whoever is raising a purchase or subcontract order. When it is off, only approvers see it.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'The estimated rate on pulled source lines is withheld from users who cannot approve.',
  }),
  def({
    key: 'default_labour_item',
    group: 'Estimation and rates',
    label: 'Default labour item for installation',
    blurb:
      'When you add a material-and-labour cost line to an estimate, the labour item configured on the item category is used. Where a category has none, this one is.',
    kind: 'lookup',
    defaultValue: '',
    lookup: { entity: 'item' },
    effect: "A BOQ import's labour leg falls back to this item when neither the sheet nor the material's category names one.",
  }),
  def({
    key: 'default_progress_method',
    group: 'Estimation and rates',
    label: 'Default BOQ progress calculation method',
    blurb:
      'When a BOQ line has several billable estimation lines, this decides how progress against it is worked out. You can still change it per project.',
    kind: 'select',
    defaultValue: 'CUMULATIVE',
    options: [
      { value: 'CUMULATIVE', label: 'Cumulative — add every line up' },
      { value: 'MINIMUM', label: 'Minimum — the least complete line governs' },
    ],
    effect: 'Billing reconciliation uses this to turn estimation progress into BOQ progress.',
  }),
  def({
    key: 'rfq_enabled',
    group: 'Estimation and rates',
    label: 'Enable RFQ',
    blurb:
      'Issue requests for quote and record supplier responses as quotations, so comparing quotations is done here rather than in a mailbox.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Requests for quote and supplier quotations appear on the Transactions hub.',
  }),

  /* ---- numbering and money ------------------------------------------ */
  def({
    key: 'manual_numbering_projects',
    group: 'Numbering and money',
    label: 'Enable manual document numbering for projects',
    blurb:
      'When this is on, you can skip the numbering rules and enter a project number yourself when you need to.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project can be submitted with a number you typed instead of the next in the series.',
  }),
  def({
    key: 'manual_numbering_invoices',
    group: 'Numbering and money',
    label: 'Enable manual document numbering for sales invoices and credit notes',
    blurb: 'The same, for sales invoices and credit notes.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A sales invoice or credit note can be submitted with a number you typed.',
  }),
  def({
    key: 'line_discount_enabled',
    group: 'Numbering and money',
    label: 'Enable the discount option in projects and sales invoices',
    blurb: 'When this is on, you can enter a discount percentage against a BOQ rate.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'The discount column is shown on line grids.',
  }),
  def({
    key: 'overall_discount_enabled',
    group: 'Numbering and money',
    label: 'Enable an overall discount',
    blurb:
      'Use one discount for the whole document instead of a discount per line. If you turn this on, make sure your print formats are updated.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A document-level discount is accepted and shown in the totals.',
  }),

  /* ---- what the rules allow ------------------------------------------ */
  def({
    key: 'escalate_exceptions_to_director',
    group: 'What the rules allow',
    label: 'Send overridden exceptions to a director',
    blurb:
      'When somebody waves past an excess quantity or buys a non-tendered item, add a final '
      + 'approval stage so a director sees it before the document is live. Without this, the '
      + 'person raising the document is also the person who signs for the exception.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'An overridden exception adds a "Director — exception approval" stage to the workflow.',
  }),
  def({
    key: 'exception_approver_role',
    group: 'What the rules allow',
    label: 'Who signs for an overridden exception',
    blurb:
      'The role the exception stage is assigned to. Only people holding it may sign that step. '
      + 'Left empty, anyone who may approve can — which is the whole point of the stage lost, so name one.',
    kind: 'lookup',
    defaultValue: '',
    lookup: { entity: 'app_role' },
    effect: 'The "Director — exception approval" stage is bound to the employees holding this role.',
  }),
  def({
    key: 'require_material_request_for_po',
    group: 'What the rules allow',
    label: 'A purchase order must come from a material request',
    blurb:
      'Refuse a purchase order line that nobody asked for. Buying without a request is how a '
      + 'project ends up with material it did not need, which is the leak this system exists to '
      + 'close. Stock and tool orders are unaffected.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project purchase order with a line raised from nothing is refused.',
  }),
  def({
    key: 'require_requisition_for_po',
    group: 'What the rules allow',
    label: 'A purchase order must come from a purchase requisition',
    blurb:
      'Buy only what the purchase executive has requisitioned. The site asks with a material '
      + 'request; purchasing decides with a requisition; the order carries that decision out. '
      + 'Stock and tool orders are unaffected.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A project purchase order with a line that is not on a purchase requisition is refused.',
  }),
  def({
    key: 'grn_inspection_required',
    group: 'What the rules allow',
    label: 'Every receipt note must be inspected',
    blurb:
      'Material is inspected at the gate before it enters stock: who inspected it is named on the receipt, '
      + 'and a receipt nobody inspected needs an approval of its own.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'A receipt note with no inspector named raises an exception that has to be approved.',
  }),
  def({
    key: 'non_tendered_needs_approval',
    group: 'What the rules allow',
    label: 'Non-tendered work needs approval before it is bought',
    blurb:
      'A BOQ line marked NT is work outside the original tender. With this on, ordering against '
      + 'one is an exception somebody with authority has to sign for.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'Ordering against a non-tendered BOQ line raises an overridable exception.',
  }),
  def({
    key: 'allow_excess_purchase',
    group: 'What the rules allow',
    label: 'Allow excess quantity in purchase',
    blurb:
      'For materials with a minimum order quantity, allow a purchase order for more than the material request asked for. We strongly recommend an extra approval step when this is on.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Excess purchase quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_receipt',
    group: 'What the rules allow',
    label: 'Allow excess quantity in receipt notes',
    blurb:
      'Receive more material than was ordered. Set the limit on the item category; beyond it, the receipt note needs special approval.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Excess receipt quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_work_progress',
    group: 'What the rules allow',
    label: 'Allow excess quantity in work progress',
    blurb:
      'Record work progress beyond the order quantity. We strongly recommend an extra approval step when this is on.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Recorded progress beyond the ordered quantity is an overridable exception rather than a refusal.',
  }),

  def({
    key: 'allow_excess_billing',
    group: 'What the rules allow',
    label: 'Allow billing beyond the BOQ quantity',
    blurb:
      'Bill a customer for more than the BOQ line ordered. Where this is off the excess is refused outright; where it is on it is an exception somebody with authority signs for.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Billing beyond the BOQ order quantity is an overridable exception rather than a refusal.',
  }),
  def({
    key: 'allow_excess_challan',
    group: 'What the rules allow',
    label: 'Allow a challan beyond what was ordered',
    blurb: 'Send out more than the order or transfer behind the challan accounted for.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'An excess challan quantity is an overridable exception rather than a refusal.',
  }),

  /* ---- integrations --------------------------------------------------- */
  def({
    key: 'einvoice_enabled',
    group: 'Integrations',
    label: 'Enable e-invoice integration',
    blurb:
      'Register sales invoices with the Invoice Registration Portal. The acknowledgement number and date are written back onto the invoice.',
    kind: 'boolean',
    defaultValue: false,
    // Registration itself is not built: there is no IRP client and no queue,
    // so the effect says only what actually happens. A setting's `effect` is
    // the record of where it is honoured, and one that describes a feature
    // nobody wrote is how a gap goes unnoticed.
    effect: 'The e-invoice acknowledgement fields are shown on a sales invoice.',
  }),
  def({
    key: 'ewaybill_enabled',
    group: 'Integrations',
    label: 'Enable e-way bill integration',
    blurb: 'Generate e-way bills for goods movements through the E-Way Bill portal.',
    kind: 'boolean',
    defaultValue: false,
    effect: 'The e-way bill number and status are shown on delivery challans and material transfers.',
  }),
  /*
    The connector itself is NOT a setting.

    `accounting_connector` and `integration_from_date` used to live here and be
    read by nothing: the live connection — which connector, from what date,
    with what credentials and over which document types — is configured on the
    Integration page and stored in `integration_config`. Two places to answer
    one question meant the Settings screen could say "Zoho Books" while nothing
    was connected. There is now one.
  */

  /* ---- access -------------------------------------------------------- */
  def({
    key: 'enforce_password_rules',
    group: 'Access',
    label: 'Enforce password rules',
    blurb:
      'Passwords must be at least eight characters and include an uppercase letter, a lowercase letter, a number and a special character.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Password strength is checked when a password is set or changed.',
  }),
  def({
    key: 'separate_submitter_and_approver',
    group: 'Access',
    label: 'The person who submits a document may not approve it',
    blurb:
      'Segregation of duties. With this off, one administrator can raise a purchase order, wave past '
      + 'an excess-quantity exception and approve the result, and the audit trail shows one name at '
      + 'every step. Switch it off only where there is genuinely nobody else to sign.',
    kind: 'boolean',
    defaultValue: true,
    effect: 'Approving a document you submitted yourself is refused.',
  }),
  def({
    key: 'ip_allow_list',
    group: 'Access',
    label: 'Restrict access to these networks',
    blurb:
      'Comma-separated IPv4 addresses or CIDR ranges. Leave this empty to allow access from anywhere. You need a static address at the office to use this.',
    kind: 'text',
    defaultValue: '',
    effect: 'A signed-in request from outside the list is refused.',
  }),
] as const satisfies readonly SettingDef[]

export type SettingKey = (typeof SETTINGS)[number]['key']

const byKey = new Map<string, SettingDef>(SETTINGS.map((s) => [s.key as string, s as SettingDef]))

export const getSettingDef = (key: string): SettingDef | undefined => byKey.get(key)

export const SETTING_GROUPS: readonly string[] = [...new Set(SETTINGS.map((s) => s.group))]

/** Every setting at its default, which is what an untouched tenant has. */
export const defaultSettings = (): Record<string, boolean | number | string> =>
  Object.fromEntries(SETTINGS.map((s) => [s.key, s.defaultValue]))

/** The shape the API answers with, and the client holds. */
export interface SettingsView {
  readonly values: Readonly<Record<string, boolean | number | string>>
  /** When and by whom each changed setting was last set. */
  readonly changed: Readonly<Record<string, { at: string; by: string | null }>>
}
